import { getEventId } from '../selectors/event';
import {
    CANNED_QUESTION_BUSINESS_TAX_ADDRESS,
    CANNED_QUESTION_TAX_ID,
    CANNED_QUESTION_TAX_INVOICE,
    TAX_EXEMPTION_STATUS_INIT,
    TAX_EXEMPTION_STATUS_INVALID_TAX_ID,
    TAX_EXEMPTION_STATUS_TAX_APPLICABLE_TAX_EXEMPTION,
    TAX_EXEMPTION_STATUS_TAX_ID_NOT_APPLICABLE_TAX_EXEMPTION,
    TAX_EXEMPTION_STATUS_VERIFYING,
    TAX_EXEMPTION_NOT_ELEGIBLE_COUNTRY,
} from '../containers/checkout/constants';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { validateTaxId } from '../api/taxExemptions';
import { saveOrder as saveOrderApi } from '../api/order';
import { selectors as orderSelectors } from '../reducers/order';
import { getAttendeeDeliveryMethodsPayloadForPlacingOrder } from '../selectors/attendees';
import { cleanUnregisteredQuestionAnswers } from '../containers/checkout/utils';
import { getFormValues } from 'redux-form';
import { CHECKOUT_FORM_NAME } from '../constants';
import { getSurvey } from '../selectors/survey';
import { updateOrder } from './order/common';

export const UPDATE_TAX_EXEMPTION_MESSAGE = 'updateTaxExemptionMessage';

export const updateTaxExemptionValidationStatus = (status) => {
    return {
        type: UPDATE_TAX_EXEMPTION_MESSAGE,
        payload: {
            status: status,
        },
    };
};

const getTaxExemptionFromState = (state) => {
    const {
        order: {
            costs: { tax: { taxBeforeExemption = null } = {}, displayTax } = {},
        },
    } = state;

    return {
        taxBeforeExemption,
        displayTax,
    };
};

const applyTaxExemption = async (dispatch, getState) => {
    const { taxBeforeExemption, displayTax } = getTaxExemptionFromState(
        getState(),
    );

    const hasTaxExemptionApplied = taxBeforeExemption;

    if (hasTaxExemptionApplied) {
        dispatch(
            updateTaxExemptionValidationStatus(
                TAX_EXEMPTION_STATUS_TAX_APPLICABLE_TAX_EXEMPTION,
            ),
        );
        return;
    }

    if (!displayTax) {
        dispatch(
            updateTaxExemptionValidationStatus(
                TAX_EXEMPTION_STATUS_TAX_ID_NOT_APPLICABLE_TAX_EXEMPTION,
            ),
        );
        return;
    }

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());

    orderUpdated.response.costs.tax.displayTax = displayTax;
    orderUpdated.response.costs.tax.taxBeforeExemption = displayTax.tax.value;

    dispatch(updateOrder(orderUpdated.response));
    dispatch(
        updateTaxExemptionValidationStatus(
            TAX_EXEMPTION_STATUS_TAX_APPLICABLE_TAX_EXEMPTION,
        ),
    );
};

const revokeTaxExemption = () => async (dispatch, getState) => {
    const { taxBeforeExemption } = getTaxExemptionFromState(getState());
    const revokesExemption = taxBeforeExemption;

    if (!revokesExemption) {
        return;
    }

    const orderUpdated = await dispatch(saveOrderWithTaxExemption(true));

    orderUpdated.response.costs.tax.taxBeforeExemption = null;

    dispatch(updateOrder(orderUpdated.response));
    dispatch(updateTaxExemptionValidationStatus(TAX_EXEMPTION_STATUS_INIT));
};

/**
 * Validates current tax ID and country and updates the message provided to the user.
 */
export const checkTaxExemptionApplies = () => async (dispatch, getState) => {
    const {
        form: { checkoutForm: { values: { buyer = [] } = {} } = {} },
    } = getState();

    const taxId = buyer[CANNED_QUESTION_TAX_ID];

    const { country: countryTaxAddress = '' } =
        buyer[CANNED_QUESTION_BUSINESS_TAX_ADDRESS] || {};

    const isBusiness = buyer[CANNED_QUESTION_TAX_INVOICE] === '1';

    if (!isBusiness) {
        dispatch(revokeTaxExemption());
        dispatch(updateTaxExemptionValidationStatus(TAX_EXEMPTION_STATUS_INIT));
        return;
    }

    if (isEmpty(taxId)) {
        dispatch(revokeTaxExemption());
        dispatch(updateTaxExemptionValidationStatus(TAX_EXEMPTION_STATUS_INIT));
        return;
    }

    dispatch(
        updateTaxExemptionValidationStatus(TAX_EXEMPTION_STATUS_VERIFYING),
    );

    const { is_tax_exempt, valid } = await validateTaxId(
        getEventId(getState()),
        countryTaxAddress,
        taxId,
    );

    if (!valid) {
        dispatch(revokeTaxExemption());
        dispatch(
            updateTaxExemptionValidationStatus(
                TAX_EXEMPTION_STATUS_INVALID_TAX_ID,
            ),
        );
        return;
    }

    if (!is_tax_exempt) {
        dispatch(revokeTaxExemption());
        dispatch(
            updateTaxExemptionValidationStatus(
                TAX_EXEMPTION_STATUS_TAX_ID_NOT_APPLICABLE_TAX_EXEMPTION,
            ),
        );
        return;
    }

    try {
        dispatch(applyTaxExemption);
    } catch (error) {
        dispatch(revokeTaxExemption());
        dispatch(
            updateTaxExemptionValidationStatus(
                TAX_EXEMPTION_STATUS_TAX_ID_NOT_APPLICABLE_TAX_EXEMPTION,
            ),
        );
    }
};

export const getFormDataForTaxExemption = (state) => {
    const formValues = getFormValues(CHECKOUT_FORM_NAME)(state);
    const survey = getSurvey(state);
    const formDataForTaxExemption = cleanUnregisteredQuestionAnswers(
        formValues,
        survey,
    );
    // remove all buyer questions but tax related
    if (formDataForTaxExemption.buyer) {
        formDataForTaxExemption.buyer = pick(formDataForTaxExemption.buyer, [
            CANNED_QUESTION_TAX_INVOICE,
            CANNED_QUESTION_TAX_ID,
            CANNED_QUESTION_BUSINESS_TAX_ADDRESS,
        ]);
    }
    return formDataForTaxExemption;
};

export const saveOrderWithTaxExemption = (revokesExemption) => {
    return (dispatch, getState) => {
        const state = getState();
        const orderId = orderSelectors.getOrderId(state.order);
        const deliveryMethods = getAttendeeDeliveryMethodsPayloadForPlacingOrder(
            state,
        );
        const formData = getFormDataForTaxExemption(state);

        return saveOrderApi({
            orderId,
            formData,
            deliveryMethods,
        });
    };
};
